import { useEffect, useRef, useState } from "react";
import { salonInfo, navLinks } from "@/mocks/content";
import SpaButton from "@/components/base/SpaButton";

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="2" width="20" height="20" rx="5" /><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const LineIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M21 11.5C21 7.36 16.97 4 12 4S3 7.36 3 11.5c0 3.69 3.27 6.79 7.69 7.38.3.06.71.2.81.45.09.23.06.58.03.81l-.13.79c-.04.23-.18.9.79.49s5.22-3.08 7.12-5.27A6.6 6.6 0 0021 11.5z" />
  </svg>
);

export default function CtaFooter() {
  const ctaRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* CTA Section */}
      <section
        id="cta"
        ref={ctaRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20luxury%20spa%20interior%20with%20dramatic%20lighting%2C%20deep%20charcoal%20stone%20walls%2C%20warm%20amber%20accent%20lights%2C%20minimalist%20japanese%20design%2C%20serene%20and%20sophisticated%20atmosphere%2C%20no%20people%2C%20moody%20dark%20tones&width=1920&height=1080&seq=cta1&orientation=landscape"
            alt="ご予約背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#2C2C2C]/80" />
        </div>

        <div
          className="relative z-10 text-center px-6"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
          }}
        >
          <p
            className="text-[#8B7355] text-xs tracking-[0.5em] mb-6 font-light"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            RESERVATION &amp; INQUIRY
          </p>
          <h2
            className="text-[#FAFAF8] font-light mb-4 leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "clamp(2rem,5vw,4rem)" }}
          >
            ご予約・お問い合わせ
          </h2>
          <p
            className="text-[#FAFAF8]/60 text-sm font-light mb-12 max-w-md mx-auto leading-relaxed"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            お電話にてご予約を承っております。<br />
            初めてのお客様もお気軽にご連絡ください。
          </p>

          <SpaButton href={`tel:${salonInfo.phone}`} variant="ghost">
            <div className="w-5 h-5 flex items-center justify-center">
              <PhoneIcon />
            </div>
            <span>{salonInfo.phone}</span>
          </SpaButton>

          <div className="mt-8 space-y-1">
            {salonInfo.hours.map((h) => (
              <p key={h.day} className="text-[#FAFAF8]/40 text-xs font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                {h.day}　{h.time}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3A3530] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <p
                className="text-[#FAFAF8] text-lg tracking-[0.2em] font-light mb-4"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {salonInfo.name}
              </p>
              <p
                className="text-[#FAFAF8]/50 text-xs leading-relaxed font-light max-w-xs"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {salonInfo.tagline}
              </p>
              <div className="flex items-center gap-4 mt-6">
                {[
                  { href: salonInfo.instagram, icon: <InstagramIcon />, label: "Instagram" },
                  { href: salonInfo.line, icon: <LineIcon />, label: "LINE" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    aria-label={s.label}
                    className="w-9 h-9 flex items-center justify-center border border-[#FAFAF8]/20 text-[#FAFAF8]/60 hover:border-[#8B7355] hover:text-[#8B7355] transition-all duration-300 cursor-pointer"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Nav */}
            <div>
              <p className="text-[#8B7355] text-xs tracking-[0.3em] mb-5 font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>MENU</p>
              <nav className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => handleNav(link.href)}
                    className="text-left text-[#FAFAF8]/60 text-sm font-light hover:text-[#8B7355] transition-colors duration-300 cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Contact */}
            <div>
              <p className="text-[#8B7355] text-xs tracking-[0.3em] mb-5 font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>CONTACT</p>
              <div className="flex flex-col gap-3">
                <a
                  href={`tel:${salonInfo.phone}`}
                  className="text-[#FAFAF8]/60 text-sm font-light hover:text-[#8B7355] transition-colors duration-300 cursor-pointer"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {salonInfo.phone}
                </a>
                <p className="text-[#FAFAF8]/40 text-xs font-light leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {salonInfo.address}
                </p>
                {salonInfo.hours.map((h) => (
                  <p key={h.day} className="text-[#FAFAF8]/40 text-xs font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {h.day}　{h.time}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-[#FAFAF8]/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[#FAFAF8]/30 text-xs font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              &copy; {new Date().getFullYear()} {salonInfo.name}. All rights reserved.
            </p>
            <p className="text-[#FAFAF8]/20 text-xs font-light" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              SHIBUYA, TOKYO
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
